import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2, Zap, Shield, Clock, Bot, BarChart3, Users, Brain, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const AgenticAISales = () => {
  const scrollToDemo = () => {
    window.location.href = '/?scrollTo=demo';
  };

  return (
    <Layout>
      <SEOHead
        title="Agentic AI for Sales Teams | Autonomous Revenue Operations | NoQ5"
        description="NoQ5 is the agentic AI platform for sales teams. Autonomous CRM updates, deal intelligence, pipeline management, and meeting insights. No manual input needed. GDPR-compliant."
        keywords="agentic AI sales, AI agent for sales, autonomous sales AI, AI workflow automation sales, agentic AI CRM, AI sales operations, autonomous revenue operations, AI sales assistant, intelligent sales agent"
        canonicalUrl="https://noq5.ai/agentic-ai-sales"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Agentic AI for Sales Teams",
          "description": "NoQ5 is the agentic AI platform for sales teams. Autonomous CRM updates, deal intelligence, pipeline management, and meeting insights.",
          "url": "https://noq5.ai/agentic-ai-sales",
          "publisher": { "@type": "Organization", "name": "NoQ5.ai", "url": "https://noq5.ai" }
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is agentic AI for sales?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Agentic AI for sales refers to AI systems that take autonomous actions on behalf of sales teams. Unlike basic chatbots or copilots, agentic AI can independently update CRM records, run outreach sequences, score deals, generate forecasts, and send alerts without human prompting. NoQ5 is built on this agentic architecture."
              }
            },
            {
              "@type": "Question",
              "name": "How does agentic AI differ from traditional sales automation?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Traditional sales automation follows rigid rules (if X then Y). Agentic AI observes context from emails, meetings, and CRM data, reasons about what needs to happen, and takes action autonomously. NoQ5 captures meeting insights, updates deal stages, identifies pipeline risks, and runs SDR outreach without manual workflows."
              }
            },
            {
              "@type": "Question",
              "name": "Is agentic AI safe for CRM operations?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "NoQ5 provides full audit trails for every automated action, one-click undo capabilities, and GDPR-compliant data handling. Every CRM update is logged with the original evidence, so revenue leaders maintain full control and visibility."
              }
            }
          ]
        }]}
      />

      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/8 border border-primary/15 mb-6">
              <Bot className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground/80">Agentic AI for Revenue Teams</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              Sales AI That Acts,<br />Not Just Advises
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              NoQ5 is the agentic AI platform that autonomously runs your revenue operations. It updates your CRM, preps your reps, scores your pipeline, books meetings, and runs outreach sequences. Your team sells. NoQ5 handles everything else.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-xl" onClick={scrollToDemo}>
                Talk to Sales <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" className="font-bold px-8 py-6 text-lg rounded-xl border-2" asChild>
                <Link to="/product/platform">See the Platform</Link>
              </Button>
            </div>
          </motion.div>

          {/* What Makes It Agentic */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-foreground text-center mb-4">What Makes NoQ5 Agentic</h2>
            <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              Traditional sales tools wait for instructions. NoQ5 observes, reasons, and acts on its own.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: RefreshCw, title: 'Autonomous CRM Updates', desc: 'After every meeting, email, or call, NoQ5 writes the right data to the right fields in HubSpot, Salesforce, or Pipedrive. No rep input needed.' },
                { icon: Brain, title: 'Intelligent Deal Scoring', desc: 'Continuously analyzes buyer engagement signals across email, calendar, and Slack to score deals and flag risks before they become losses.' },
                { icon: Bot, title: 'Smart Lead Qualification', desc: 'Automatically scores and qualifies inbound leads based on engagement signals, ICP fit, and buying intent. Prioritizes your pipeline.' },
                { icon: BarChart3, title: 'Evidence-Based Forecasting', desc: 'Generates forecasts grounded in real buyer behavior, not rep opinions. Board-ready reports with evidence trails for every number.' },
                { icon: Users, title: 'Meeting Intelligence', desc: 'Joins calls, transcribes, extracts action items, and updates your CRM. Before the next meeting, it prepares a full briefing for the rep.' },
                { icon: Shield, title: 'GDPR-First Architecture', desc: 'Built in Ireland, EU-hosted, with anonymized prompting and full data residency controls. Enterprise-ready compliance from day one.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 bg-card border border-border rounded-xl"
                >
                  <item.icon className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Agentic vs Traditional */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">Agentic AI vs. Traditional Sales Tools</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 text-foreground font-semibold">Capability</th>
                    <th className="text-center p-4 text-muted-foreground font-semibold">Traditional CRM / Automation</th>
                    <th className="text-center p-4 text-primary font-semibold">NoQ5 Agentic AI</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    ['CRM Updates', 'Manual by reps', 'Autonomous after every interaction'],
                    ['Deal Scoring', 'Rule-based stages', 'Evidence-based, 15+ buyer signals'],
                    ['Forecasting', 'Rep self-reported', 'AI-generated from real activity'],
                    ['Meeting Notes', 'Manual or basic transcription', 'Full intelligence with CRM write-back'],
                    ['SDR Outreach', 'Template sequences', 'Self-learning, multi-channel, personalized'],
                    ['Risk Alerts', 'None or delayed', 'Proactive, real-time going-dark detection'],
                    ['Setup Time', 'Weeks to months', '5 minutes, OAuth-based'],
                  ].map(([cap, trad, noq5], i) => (
                    <tr key={i} className="border-b border-border/50">
                      <td className="p-4 text-foreground font-medium">{cap}</td>
                      <td className="p-4 text-center text-muted-foreground">{trad}</td>
                      <td className="p-4 text-center text-primary font-medium">{noq5}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {[
                { q: 'What is agentic AI for sales?', a: 'Agentic AI for sales refers to AI systems that autonomously perform sales operations tasks. Unlike copilots that wait for prompts, agentic AI observes your meetings, emails, and deals, then independently updates CRM records, scores pipeline health, generates forecasts, and runs outreach. NoQ5 is built on this agentic architecture.' },
                { q: 'How does agentic AI differ from a chatbot or copilot?', a: 'Chatbots respond to questions. Copilots assist with tasks when prompted. Agentic AI proactively takes action without being asked. NoQ5 monitors your revenue operations 24/7 and autonomously handles CRM updates, deal scoring, meeting prep, and SDR outreach.' },
                { q: 'Is it safe to let AI update my CRM automatically?', a: 'Yes. Every NoQ5 action includes a full audit trail showing the evidence used, the change made, and a one-click undo option. Revenue leaders maintain full control with configurable approval workflows for sensitive operations.' },
                { q: 'Which CRMs does NoQ5 work with?', a: 'NoQ5 integrates with HubSpot, Salesforce, and Pipedrive. It also connects to Google Workspace (Calendar, Gmail, Meet), Slack, and Zoom for complete revenue data capture.' },
                { q: 'How long does setup take?', a: 'Under 5 minutes. NoQ5 connects via OAuth to your existing tools. No IT involvement, no data migration, no custom configuration needed.' },
              ].map((faq, i) => (
                <div key={i} className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Internal Links */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-foreground text-center mb-8">Explore More</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/product/platform" className="p-4 bg-card border border-border rounded-xl hover:border-primary/40 transition-colors text-center">
                <h3 className="font-semibold text-foreground mb-1">Core Platform</h3>
                <p className="text-sm text-muted-foreground">Full platform overview</p>
              </Link>
              <Link to="/crm-agent" className="p-4 bg-card border border-border rounded-xl hover:border-primary/40 transition-colors text-center">
                <h3 className="font-semibold text-foreground mb-1">AI CRM Agent</h3>
                <p className="text-sm text-muted-foreground">Autonomous CRM assistant</p>
              </Link>
              <Link to="/use-cases/crm-automation" className="p-4 bg-card border border-border rounded-xl hover:border-primary/40 transition-colors text-center">
                <h3 className="font-semibold text-foreground mb-1">CRM Automation</h3>
                <p className="text-sm text-muted-foreground">Zero-touch CRM updates</p>
              </Link>
            </div>
          </section>

          {/* Final CTA */}
          <div className="text-center p-12 bg-primary/5 border border-primary/20 rounded-2xl">
            <h2 className="text-3xl font-bold text-foreground mb-4">Ready for Agentic Revenue Operations?</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Join revenue teams using NoQ5 to automate their entire revenue workflow.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-xl" onClick={scrollToDemo}>
              Talk to Sales <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AgenticAISales;
